/**
 * AssignCrewModal Component
 *
 * Modal for assigning a crew member to a role on an event.
 * Shows available crew with their availability status.
 */

import { useState, useEffect } from 'react';
import {
  getAvailableCrew,
  assignCrewToRole,
  type AvailableCrew,
} from '../../api/assignments';
import { getErrorMessage } from '../../api/client';
import { AVAILABILITY_STATUS } from '../../config/statusStyles';
import Modal from '../ui/Modal';
import NoteTooltip from '../ui/NoteTooltip';
import CrewProfileLink from '../crew/CrewProfileLink';

interface AssignCrewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssigned: () => void;
  eventId: string;
  roleId: string;
  roleName: string;
}

export default function AssignCrewModal({
  isOpen,
  onClose,
  onAssigned,
  eventId,
  roleId,
  roleName,
}: AssignCrewModalProps) {
  const [crew, setCrew] = useState<AvailableCrew[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAvailableCrew();
    }
  }, [isOpen, eventId, roleId]);

  const loadAvailableCrew = async () => {
    setLoading(true);
    setError(null);
    try {
      const { crew } = await getAvailableCrew(eventId, roleId);
      setCrew(crew);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (userId: string) => {
    setAssigning(userId);
    setError(null);
    try {
      await assignCrewToRole(eventId, roleId, userId);
      onAssigned();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setAssigning(null);
    }
  };

  const filteredCrew = crew.filter((member) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      member.firstName.toLowerCase().includes(searchLower) ||
      member.lastName.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower)
    );
  });

  // Sort: Available first, then Maybe, then Not Available, then no response
  // (note-only rows have a null status and sort with no-response)
  const sortedCrew = [...filteredCrew].sort((a, b) => {
    const order = { AVAILABLE: 0, MAYBE: 1, NOT_AVAILABLE: 2 };
    const aOrder = a.availability?.status ? order[a.availability.status] : 3;
    const bOrder = b.availability?.status ? order[b.availability.status] : 3;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      labelledBy="assign-crew-title"
      contentClassName="max-h-[80vh] flex flex-col"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 id="assign-crew-title" className="text-lg font-semibold text-gray-900">
            Assign Crew to {roleName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="mt-4">
          <input
            type="text"
            autoFocus
            placeholder="Search crew..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 p-3">
                <div className="w-10 h-10 bg-gray-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedCrew.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No crew members match your search' : 'No available crew members'}
          </div>
        ) : (
          <div className="space-y-2">
            {sortedCrew.map((member) => {
              const availabilityInfo = member.availability?.status
                ? AVAILABILITY_STATUS[member.availability.status]
                : null;

              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-100"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                    {member.firstName.charAt(0)}
                    {member.lastName.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      <CrewProfileLink userId={member.id}>
                        {member.firstName} {member.lastName}
                      </CrewProfileLink>
                      {member.availability?.notes && (
                        <NoteTooltip
                          note={member.availability.notes}
                          label={`Availability note from ${member.firstName} ${member.lastName}`}
                        />
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{member.email}</p>
                  </div>

                  {/* Availability badge */}
                  {availabilityInfo ? (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${availabilityInfo.className}`}
                    >
                      {availabilityInfo.label}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
                      No Response
                    </span>
                  )}

                  {/* Assign button */}
                  <button
                    type="button"
                    onClick={() => handleAssign(member.id)}
                    disabled={assigning === member.id}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
                  >
                    {assigning === member.id ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{sortedCrew.length} crew member{sortedCrew.length !== 1 ? 's' : ''} available</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
